/**
 * TextLoop - Vanilla JS + SVG + GSAP Implementation
 * Adapted from React Bits for Shopify / HTML
 * Fixed for full-bleed width and crisp text rendering.
 */
(function(window) {
  const VIEW_W = 1920;
  const VIEW_H = 260;
  const CX = VIEW_W / 2;
  const CY = VIEW_H / 2;

  function buildPath(shape, curviness, ribbonWidth) {
    const c = Math.max(0, curviness);
    switch (shape) {
      case 'circle': {
        const r = Math.min(120 + c * 0.95, 200);
        return `M ${CX - r} ${CY} A ${r} ${r} 0 1 1 ${CX + r} ${CY} A ${r} ${r} 0 1 1 ${CX - r} ${CY} Z`;
      }
      case 'infinity': {
        const r = 240 + c * 1.4;
        const h = Math.min(80 + c * 0.95, 120);
        return [
          `M ${CX} ${CY}`,
          `C ${CX + r * 0.55} ${CY - h} ${CX + r} ${CY - h} ${CX + r} ${CY}`,
          `C ${CX + r} ${CY + h} ${CX + r * 0.55} ${CY + h} ${CX} ${CY}`,
          `C ${CX - r * 0.55} ${CY - h} ${CX - r} ${CY - h} ${CX - r} ${CY}`,
          `C ${CX - r} ${CY + h} ${CX - r * 0.55} ${CY + h} ${CX} ${CY}`,
          'Z'
        ].join(' ');
      }
      case 'arch': {
        const rise = Math.min(120 + c * 1.1, 200);
        return `M -200 ${CY + rise / 2} Q ${CX} ${CY - rise * 1.5} ${VIEW_W + 200} ${CY + rise / 2}`;
      }
      case 'line':
        return `M -400 ${CY} L ${VIEW_W + 400} ${CY}`;
      case 'wave':
      default: {
        const a = Math.min(c * 1.1, 100);
        return `M -400 ${CY} Q -170 ${CY - a} 60 ${CY} T 520 ${CY} T 980 ${CY} T 1440 ${CY} T 1900 ${CY} T 2360 ${CY}`;
      }
    }
  }

  class TextLoopApp {
    constructor(container, options = {}) {
      this.container = typeof container === 'string' ? document.querySelector(container) : container;
      if (!this.container) return;

      this.text = options.text || 'skyland';
      this.shape = options.shape || 'wave';
      this.pathData = options.path;
      this.speed = options.speed !== undefined ? options.speed : 90;
      this.direction = options.direction || 'forward';
      this.separator = options.separator !== undefined ? options.separator : '✦';
      this.curviness = options.curviness !== undefined ? options.curviness : 90;
      this.fontSize = options.fontSize || 42;
      this.fontWeight = options.fontWeight || 800;
      this.letterSpacing = options.letterSpacing !== undefined ? options.letterSpacing : 2;
      this.uppercase = options.uppercase !== undefined ? options.uppercase : true;
      this.color = options.color || '#ffffff';
      this.ribbon = options.ribbon !== undefined ? options.ribbon : true;
      this.ribbonColor = options.ribbonColor || '#000000';
      this.ribbonWidth = options.ribbonWidth !== undefined ? options.ribbonWidth : 86;
      this.pauseOnHover = options.pauseOnHover !== undefined ? options.pauseOnHover : true;
      this.className = options.className || '';

      this.pathId = 'text-loop-' + Math.random().toString(36).substring(2, 9);
      this.init();
    }

    init() {
      this.container.innerHTML = '';
      this.container.classList.add('text-loop-wrapper');
      if (this.className) {
        this.container.classList.add(...this.className.split(' ').filter(Boolean));
      }

      const d = this.pathData || buildPath(this.shape, this.curviness, this.ribbonWidth);
      const base = this.uppercase ? String(this.text).toUpperCase() : String(this.text);
      const gap = this.separator ? `\u00A0${this.separator}\u00A0` : '\u00A0\u00A0\u00A0';
      const unit = `${base}${gap}`;

      const svgNS = 'http://www.w3.org/2000/svg';
      const svg = document.createElementNS(svgNS, 'svg');
      svg.setAttribute('class', 'text-loop-svg');
      svg.setAttribute('viewBox', `0 0 ${VIEW_W} ${VIEW_H}`);
      svg.setAttribute('preserveAspectRatio', 'none');
      svg.setAttribute('role', 'img');
      svg.setAttribute('aria-label', this.text);

      const path = document.createElementNS(svgNS, 'path');
      path.setAttribute('id', this.pathId);
      path.setAttribute('d', d);
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', this.ribbon ? this.ribbonColor : 'none');
      path.setAttribute('stroke-width', this.ribbon ? this.ribbonWidth : '0');
      path.setAttribute('stroke-linecap', 'round');
      path.setAttribute('stroke-linejoin', 'round');
      svg.appendChild(path);

      const textStyle = `font-size:${this.fontSize}px; font-weight:${this.fontWeight}; letter-spacing:${this.letterSpacing}px; font-family: Outfit, sans-serif;`;

      const measureText = document.createElementNS(svgNS, 'text');
      measureText.setAttribute('class', 'text-loop-measure');
      measureText.setAttribute('style', textStyle);
      measureText.setAttribute('aria-hidden', 'true');
      measureText.textContent = unit;
      svg.appendChild(measureText);

      const mainText = document.createElementNS(svgNS, 'text');
      mainText.setAttribute('class', 'text-loop-text');
      mainText.setAttribute('style', textStyle);
      mainText.setAttribute('fill', this.color);
      mainText.setAttribute('dominant-baseline', 'central');
      mainText.setAttribute('aria-hidden', 'true');

      const mainPath = document.createElementNS(svgNS, 'textPath');
      mainPath.setAttribute('href', `#${this.pathId}`);
      mainPath.setAttribute('startOffset', '0');
      mainText.appendChild(mainPath);
      svg.appendChild(mainText);

      this.container.appendChild(svg);

      const startAnimation = () => {
        let length = 0;
        let unitWidth = 0;
        try {
          length = path.getTotalLength();
          unitWidth = measureText.getComputedTextLength();
        } catch(e) {
          length = VIEW_W * 1.5;
          unitWidth = 250;
        }

        if (!unitWidth || unitWidth <= 0) unitWidth = 250;
        if (!length || length <= 0) length = VIEW_W * 1.5;

        // Repeat unit enough times to cover path length + 3 extra units
        const reps = Math.max(4, Math.ceil(length / unitWidth) + 3);
        const loopText = unit.repeat(reps);

        mainPath.textContent = loopText;

        const setOffset = (val) => {
          mainPath.setAttribute('startOffset', `${val}px`);
        };

        const isReverse = this.direction === 'reverse';
        const startVal = isReverse ? -unitWidth : 0;
        const endVal = isReverse ? 0 : -unitWidth;
        const targetDuration = Math.max(1, unitWidth / (this.speed || 50));

        setOffset(startVal);

        if (window.gsap && this.speed > 0) {
          const state = { offset: startVal };
          this.tween = window.gsap.to(state, {
            offset: endVal,
            duration: targetDuration,
            ease: 'none',
            repeat: -1,
            onUpdate: () => setOffset(state.offset)
          });

          if (this.pauseOnHover) {
            this.container.addEventListener('pointerenter', () => this.tween && this.tween.pause());
            this.container.addEventListener('pointerleave', () => this.tween && this.tween.resume());
          }
        }
      };

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(startAnimation).catch(startAnimation);
      } else {
        setTimeout(startAnimation, 100);
      }
    }
  }

  window.TextLoopApp = TextLoopApp;
})(window);
